import {
  AlertTriangle,
  Check,
  Edit3,
  ImagePlus,
  Loader2,
  Search,
  Shield,
  Trash2,
  UserPlus,
  Users,
  X,
  CalendarClock,
  Plus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AxiosError } from "axios";
import type { AdminPanelUser, UserSchedulePayload } from "../types/admin-panel";
import {
  createAdminPanelUser,
  deleteAdminPanelUser,
  updateAdminPanelUser,
  type SaveUserPayload,
} from "../api/admin-panel";
import { useRoles } from "@modules/settings/api";
import { useDepartamentos } from "@modules/settings/api/rrhh";
import { useIssuers } from "@modules/billing/api/issuers";

type Props = {
  users: AdminPanelUser[];
  loading: boolean;
  onReload: () => Promise<void> | void;
};

// Roles por defecto del CRM/ERP. Si el usuario tiene permiso para gestionar roles,
// se sustituye en runtime por la lista real (incluye roles personalizados del cliente).
const DEFAULT_ROLES = [
  "admin",
  "coordinador",
  "gerente",
  "comercial",
  "marketing",
  "finanzas",
  "administracion",
  "rrhh",
  "soporte",
  "empleado",
];

// Fallback si no se pueden cargar los departamentos configurables.
const DEFAULT_DEPARTMENTS = [
  "Direccion",
  "Supervision OTG-OTM",
  "Supervision OTR-OTM",
  "OTR",
  "OTG",
  "OTM",
  "Informatica",
];

function normalizeCompanyKey(value?: string | null) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]/g, "");
}

function isInpronetCompany(value?: string | null) {
  return normalizeCompanyKey(value).includes("inpronetsolutions");
}

const WEEK_DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 7, label: "Domingo" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function defaultSchedules(): UserSchedulePayload[] {
  const currentMonth = new Date().getMonth() + 1;
  const isSummerMonth = currentMonth === 7 || currentMonth === 8;
  const defaultFridayEnd = isSummerMonth ? "14:30" : "15:00";

  return [
    {
      dia_semana: 1,
      hora_inicio: "07:30",
      hora_fin: "15:30",
      tolerancia_minutos: 15,
      activo: true,
      vigente_desde: todayIso(),
      vigente_hasta: null,
    },
    {
      dia_semana: 2,
      hora_inicio: "07:30",
      hora_fin: "15:30",
      tolerancia_minutos: 15,
      activo: true,
      vigente_desde: todayIso(),
      vigente_hasta: null,
    },
    {
      dia_semana: 3,
      hora_inicio: "07:30",
      hora_fin: "15:30",
      tolerancia_minutos: 15,
      activo: true,
      vigente_desde: todayIso(),
      vigente_hasta: null,
    },
    {
      dia_semana: 4,
      hora_inicio: "07:30",
      hora_fin: "15:30",
      tolerancia_minutos: 15,
      activo: true,
      vigente_desde: todayIso(),
      vigente_hasta: null,
    },
    {
      dia_semana: 5,
      hora_inicio: "07:30",
      hora_fin: defaultFridayEnd,
      tolerancia_minutos: 15,
      activo: true,
      vigente_desde: todayIso(),
      vigente_hasta: null,
    },
  ];
}

const emptyForm: SaveUserPayload = {
  nombre: "",
  correo: "",
  rol: "empleado",
  contrasena: "",
  foto: null,
  departamento: "",
  empresa: "",
  fecha_nacimiento: "",
  fecha_inicio_contratacion: "",
  vacation_partner_ids: [],
  schedules: defaultSchedules(),
};

export function UsersPanel({ users, loading, onReload }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminPanelUser | null>(null);
  const [form, setForm] = useState<SaveUserPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminPanelUser | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [error, setError] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserRole, setSelectedUserRole] = useState("all");
  const { data: rolesData } = useRoles();
  const roleOptions = useMemo(
    () =>
      rolesData && rolesData.length
        ? rolesData.map((r) => r.name)
        : DEFAULT_ROLES,
    [rolesData],
  );
  const { data: emisores } = useIssuers();
  const selectedEmisorId = useMemo(() => {
    const selectedCompanyKey = normalizeCompanyKey(form.empresa);
    if (!selectedCompanyKey) return null;

    return (
      emisores?.find((emisor) => normalizeCompanyKey(emisor.nombre) === selectedCompanyKey)?.id ?? null
    );
  }, [emisores, form.empresa]);
  const { data: depsData } = useDepartamentos(selectedEmisorId);
  const departmentOptions = useMemo(() => {
    if (!selectedEmisorId) return [];
    if (depsData && depsData.length) {
      return depsData.filter((department) => department.activo).map((department) => department.nombre);
    }

    return isInpronetCompany(form.empresa) ? DEFAULT_DEPARTMENTS : [];
  }, [depsData, form.empresa, selectedEmisorId]);
  const roleCounts = useMemo(() => {
    const counts = new Map<string, number>();

    users.forEach((user) => {
      counts.set(user.rol, (counts.get(user.rol) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([role, count]) => ({ role, count }))
      .sort((left, right) => {
        if (left.role === "admin") return -1;
        if (right.role === "admin") return 1;
        return roleLabel(left.role).localeCompare(roleLabel(right.role), "es");
      });
  }, [users]);
  const adminsCount = useMemo(
    () => users.filter((user) => user.rol.includes("admin")).length,
    [users],
  );
  const additionalRoleCards = useMemo(
    () => roleCounts.filter(({ role }) => !role.includes("admin")),
    [roleCounts],
  );
  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      vacation_partner_ids: [],
      schedules: defaultSchedules(),
    });
    setPartnerSearch("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (user: AdminPanelUser) => {
    setEditing(user);
    setForm({
      nombre: user.nombre,
      correo: user.correo,
      rol: user.rol,
      contrasena: "",
      foto: null,
      departamento: user.departamento ?? "",
      empresa: user.empresa ?? "",
      fecha_nacimiento: user.fecha_nacimiento ?? "",
      fecha_inicio_contratacion: user.fecha_inicio_contratacion ?? "",
      vacation_partner_ids: user.vacation_partner_ids ?? [],
      schedules:
        user.schedules && user.schedules.length > 0
          ? user.schedules
          : defaultSchedules(),
    });
    setPartnerSearch("");
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm({ ...emptyForm, schedules: defaultSchedules() });
    setError("");
  };

  const filteredUsers = useMemo(() => {
    const value = userSearch.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !value ||
        user.nombre.toLowerCase().includes(value) ||
        user.correo.toLowerCase().includes(value);
      const matchesRole =
        selectedUserRole === "all" || user.rol === selectedUserRole;

      return matchesSearch && matchesRole;
    });
  }, [selectedUserRole, users, userSearch]);

  const saveUser = async () => {
    setError("");

    if (!form.nombre.trim() || !form.correo.trim() || !form.rol) {
      setError("Nombre, correo y rol son obligatorios.");
      return;
    }

    const password = form.contrasena?.trim() ?? "";

    if (!editing && !password) {
      setError("La contraseña es obligatoria.");
      return;
    }

    if (!editing && password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (editing && password && password.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }

    setSaving(true);

    try {
      const payload: SaveUserPayload = {
        nombre: form.nombre.trim(),
        correo: form.correo.trim(),
        rol: form.rol,
        foto: form.foto ?? null,
        departamento: form.departamento || null,
        empresa: form.empresa?.trim() || null,
        fecha_nacimiento: form.fecha_nacimiento || null,
        fecha_inicio_contratacion: form.fecha_inicio_contratacion || null,
        vacation_partner_ids: Array.from(
          new Set(form.vacation_partner_ids ?? []),
        ),
        schedules: form.schedules ?? [],
      };

      if (form.contrasena?.trim()) {
        payload.contrasena = form.contrasena.trim();
      }

      if (editing) {
        await updateAdminPanelUser(editing.id, payload);
      } else {
        await createAdminPanelUser(payload);
      }

      await onReload();
      closeModal();
    } catch (error) {
      if (error instanceof AxiosError) {
        const responseData = error.response?.data as
          | {
              message?: string;
              errors?: Record<string, string[]>;
            }
          | undefined;

        const firstValidationError = responseData?.errors
          ? Object.values(responseData.errors).flat()[0]
          : null;

        setError(
          firstValidationError ??
            responseData?.message ??
            "No se pudo guardar el usuario.",
        );
      } else {
        setError("No se pudo guardar el usuario.");
      }
    } finally {
      setSaving(false);
    }
  };

  const removeUser = (user: AdminPanelUser) => {
    setDeleteError("");
    setDeleteTarget(user);
  };

  const confirmRemoveUser = async () => {
    if (!deleteTarget) return;

    setDeletingId(deleteTarget.id);
    setDeleteError("");

    try {
      await deleteAdminPanelUser(deleteTarget.id);
      setDeleteTarget(null);
      await onReload();
    } catch (requestError: unknown) {
      const apiError = requestError as AxiosError<{ message?: string }>;
      setDeleteError(apiError.response?.data?.message ?? "No se pudo eliminar el usuario.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="scroll-reveal-left flex h-full flex-col gap-6">
      <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
        <div className="dashboard-scale-in group relative overflow-hidden rounded-[34px] border border-[#d7e5df] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-6 shadow-[0_24px_70px_rgba(15,23,42,.09)] backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-emerald-200 hover:shadow-[0_38px_100px_rgba(15,23,42,.14)]">
          <div className="pointer-events-none absolute right-[-70px] top-[-80px] h-56 w-56 rounded-full bg-emerald-300/22 blur-3xl transition duration-500 group-hover:scale-110" />
          <div className="pointer-events-none absolute bottom-[-90px] left-[-70px] h-56 w-56 rounded-full bg-teal-300/14 blur-3xl transition duration-500 group-hover:scale-110" />

          <div className="relative flex items-start gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-3xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_12px_28px_rgba(16,185,129,.10)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
              <Users size={24} />
            </div>

            <div className="flex-1">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                Workforce management
              </div>

              <h3 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                Gestión de empleados
              </h3>

              <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
                Administra accesos, roles y permisos de los usuarios desde un
                panel claro, moderno y preparado para equipos internos.
              </p>

              <div className="mt-5 flex flex-wrap gap-2.5 pb-1">
                <MetricPill label="Usuarios" value={String(users.length)} />
                <MetricPill label="Administradores" value={String(adminsCount)} />
                {additionalRoleCards.map(({ role, count }) => (
                  <MetricPill
                    key={role}
                    label={roleLabel(role)}
                    value={String(count)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-slide-right group rounded-[34px] border border-[#d7e5df] bg-[linear-gradient(180deg,#ffffff_0%,#f7fbf8_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,.08)] backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-emerald-200 hover:shadow-[0_34px_90px_rgba(15,23,42,.14)] lg:min-w-[260px]">
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
            Acciones
          </div>

          <div className="mt-2 text-lg font-black text-slate-950">
            Usuarios internos
          </div>

          <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
            Crea y administra accesos rápidamente.
          </p>

          <button
            onClick={openCreate}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] transition-all duration-300 hover:-translate-y-1.5 hover:scale-[1.02] hover:shadow-[0_28px_60px_rgba(16,185,129,.30)] active:scale-[0.98]"
          >
            <UserPlus size={16} />
            Añadir empleado
          </button>
        </div>
      </div>

      <div className="dashboard-scale-in overflow-hidden rounded-[34px] border border-[#d7e5df] bg-white shadow-[0_24px_70px_rgba(15,23,42,.10)] backdrop-blur-xl">
        <div className="border-b border-[#edf1f3] px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div>
                <div className="mb-2 text-sm font-black text-slate-950">
                  Directorio corporativo
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_220px]">
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <Search size={16} className="text-slate-400" />
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Buscar usuario..."
                    className="min-w-0 flex-1 bg-transparent text-sm font-bold text-slate-700 outline-none"
                  />
                </div>

                <select
                  value={selectedUserRole}
                  onChange={(event) => setSelectedUserRole(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 outline-none"
                  aria-label="Filtrar usuarios por rol"
                >
                  <option value="all">Todos los roles</option>
                  {roleCounts.map(({ role, count }) => (
                    <option key={role} value={role}>
                      {roleLabel(role)} ({count})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-1 text-sm font-semibold text-slate-500">
                Usuarios registrados dentro del sistema.
              </div>
            </div>

            <div className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
              {filteredUsers.length === users.length
                ? `${users.length} registros`
                : `${filteredUsers.length} de ${users.length}`}
            </div>
          </div>
        </div>

        <div className="max-h-[560px] overflow-auto">
          <table className="w-full min-w-[880px] border-separate border-spacing-0">
            <thead className="sticky top-0 z-10">
              <tr>
                {["Empleado", "Rol", "Última actividad", "Acciones"].map(
                  (h) => (
                    <th
                      key={h}
                      className="border-b border-[#edf1f3] bg-slate-50/80 px-5 py-4 text-left text-xs font-black uppercase tracking-[0.14em] text-slate-400"
                    >
                      <span
                        className={h === "Acciones" ? "block text-right" : ""}
                      >
                        {h}
                      </span>
                    </th>
                  ),
                )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <SkeletonRows cols={4} />
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-16 text-center">
                    <div className="mx-auto max-w-sm">
                      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
                        <Users size={22} />
                      </div>

                      <div className="mt-4 text-lg font-black text-slate-950">
                        No hay usuarios registrados
                      </div>

                      <p className="mt-2 text-sm font-semibold text-slate-500">
                        Crea el primer usuario para comenzar.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="group transition-all duration-300 hover:scale-[1.002]"
                  >
                    <td className="border-b border-[#edf1f3] px-5 py-4 transition-all duration-300 group-hover:bg-emerald-50/45">
                      <div className="flex items-center gap-4">
                        <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl border border-emerald-100 bg-emerald-50 text-sm font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.08)] transition-all duration-300 group-hover:-rotate-6 group-hover:scale-110 group-hover:shadow-[0_18px_36px_rgba(16,185,129,.18)]">
                          {getUserPhotoUrl(user) ? (
                            <img
                              src={getUserPhotoUrl(user) ?? ""}
                              alt={`Foto de ${user.nombre}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            getInitials(user.nombre)
                          )}
                        </div>

                        <div>
                          <div className="font-black text-slate-950">
                            {user.nombre}
                          </div>

                          <div className="mt-1 text-sm font-semibold text-slate-500">
                            {user.correo}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="border-b border-[#edf1f3] px-5 py-4 transition-all duration-300 group-hover:bg-emerald-50/45">
                      <RoleBadge role={user.rol} />
                    </td>

                    <td className="border-b border-[#edf1f3] px-5 py-4 transition-all duration-300 group-hover:bg-emerald-50/45">
                      <div className="text-sm font-semibold text-slate-500">
                        {user.last_activity
                          ? formatDate(user.last_activity)
                          : "Sin actividad"}
                      </div>
                    </td>

                    <td className="border-b border-[#edf1f3] px-5 py-4 transition-all duration-300 group-hover:bg-emerald-50/45">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(user)}
                          className="grid h-10 w-10 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_10px_24px_rgba(15,23,42,.05)] transition-all duration-300 hover:-translate-y-1.5 hover:rotate-[-6deg] hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-[0_18px_36px_rgba(16,185,129,.12)]"
                          title="Editar"
                        >
                          <Edit3 size={16} />
                        </button>

                        <button
                          onClick={() => removeUser(user)}
                          disabled={deletingId === user.id}
                          className="grid h-10 w-10 place-items-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-500 shadow-[0_10px_24px_rgba(244,63,94,.06)] transition-all duration-300 hover:-translate-y-1.5 hover:rotate-[6deg] hover:bg-rose-100 hover:text-rose-600 hover:shadow-[0_18px_36px_rgba(244,63,94,.14)] disabled:cursor-not-allowed disabled:opacity-60"
                          title="Eliminar"
                        >
                          {deletingId === user.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-slate-950/30 p-4 backdrop-blur-md">
          <div className="dashboard-scale-in flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[38px] border border-[#d7e5df] bg-white shadow-[0_44px_120px_rgba(15,23,42,.22)]">
            <div className="shrink-0 border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] px-7 py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="grid h-14 w-14 place-items-center rounded-3xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_12px_28px_rgba(16,185,129,.10)]">
                    <Shield size={24} />
                  </div>

                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                      Seguridad y accesos
                    </div>

                    <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                      {editing ? "Editar empleado" : "Nuevo empleado"}
                    </h3>

                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      Gestiona permisos y accesos corporativos.
                    </p>
                  </div>
                </div>

                <button
                  onClick={closeModal}
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-7">
              <div className="grid gap-5 md:grid-cols-2">
                <Field
                  label="Nombre completo"
                  value={form.nombre}
                  onChange={(v) => setForm((s) => ({ ...s, nombre: v }))}
                />

                <Field
                  label="Correo corporativo"
                  type="email"
                  value={form.correo}
                  onChange={(v) => setForm((s) => ({ ...s, correo: v }))}
                />
              </div>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Rol y permisos
                </span>

                <select
                  value={form.rol}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, rol: e.target.value }))
                  }
                  className="field-control"
                >
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>

              <div className="grid gap-5 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                    Empresa (emisor)
                  </span>

                  <select
                    value={form.empresa ?? ""}
                    onChange={(e) => {
                      const empresa = e.target.value;
                      setForm((current) => ({
                        ...current,
                        empresa,
                        departamento: empresa === current.empresa ? current.departamento : "",
                      }));
                    }}
                    className="field-control"
                  >
                    <option value="">Sin empresa</option>
                    {emisores?.map((em) => (
                      <option key={em.id} value={em.nombre}>
                        {em.nombre}
                      </option>
                    ))}
                    {form.empresa &&
                      !emisores?.some((em) => em.nombre === form.empresa) && (
                        <option value={form.empresa}>{form.empresa} (antiguo)</option>
                      )}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                    Departamento
                  </span>

                  <select
                    value={form.departamento ?? ""}
                    onChange={(e) =>
                      setForm((current) => ({ ...current, departamento: e.target.value }))
                    }
                    disabled={!form.empresa}
                    className="field-control disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">{form.empresa ? "Sin departamento" : "Selecciona una empresa"}</option>
                    {departmentOptions.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                    {form.departamento &&
                      !departmentOptions.includes(form.departamento) && (
                        <option value={form.departamento}>{form.departamento} (antiguo)</option>
                      )}
                  </select>
                </label>
              </div>

              <div className="grid gap-5 md:grid-cols-3">
                <FileField
                  label="Foto usuario"
                  file={form.foto ?? null}
                  currentPath={editing?.foto_path ?? null}
                  onChange={(file) => setForm((current) => ({ ...current, foto: file }))}
                />

                <Field
                  label="Fecha nacimiento"
                  type="date"
                  value={form.fecha_nacimiento ?? ""}
                  onChange={(v) =>
                    setForm((current) => ({ ...current, fecha_nacimiento: v }))
                  }
                />

                <Field
                  label="Inicio contratación"
                  type="date"
                  value={form.fecha_inicio_contratacion ?? ""}
                  onChange={(v) =>
                    setForm((current) => ({ ...current, fecha_inicio_contratacion: v }))
                  }
                />
              </div>

              <ScheduleEditor
                schedules={form.schedules ?? []}
                onChange={(schedules) =>
                  setForm((current) => ({
                    ...current,
                    schedules,
                  }))
                }
              />

              <PartnerSelector
                users={users}
                editingId={editing?.id ?? null}
                selectedIds={form.vacation_partner_ids ?? []}
                search={partnerSearch}
                onSearch={setPartnerSearch}
                onToggle={(partnerId) =>
                  setForm((current) => ({
                    ...current,
                    vacation_partner_ids: togglePartnerId(
                      current.vacation_partner_ids ?? [],
                      partnerId,
                    ),
                  }))
                }
              />

              <Field
                label={editing ? "Nueva contraseña opcional" : "Contraseña"}
                type="password"
                autoComplete={editing ? "new-password" : "new-password"}
                value={form.contrasena ?? ""}
                onChange={(v) => setForm((s) => ({ ...s, contrasena: v }))}
              />
            </div>

            <div className="shrink-0 flex flex-col gap-3 border-t border-[#edf1f3] bg-slate-50/80 px-7 py-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-h-[44px] flex-1">
                {error && (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={closeModal}
                  disabled={saving}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancelar
                </button>

                <button
                  onClick={saveUser}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.20)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_42px_rgba(16,185,129,.26)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  Guardar usuario
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {deleteTarget ? createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="admin-delete-user-title"
            className="w-full max-w-lg overflow-hidden rounded-[32px] border border-rose-100 bg-white shadow-[0_40px_110px_rgba(15,23,42,.24)]"
          >
            <div className="flex items-start gap-4 border-b border-rose-100 bg-rose-50/70 px-6 py-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-rose-100 text-rose-600">
                <AlertTriangle size={22} />
              </span>
              <div>
                <h3 id="admin-delete-user-title" className="text-xl font-black text-slate-950">
                  Eliminar usuario
                </h3>
                <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">
                  Se eliminará la cuenta de <strong>{deleteTarget.nombre}</strong>. Esta acción no se puede deshacer.
                </p>
              </div>
            </div>

            <div className="px-6 py-5">
              {deleteError ? (
                <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
                  {deleteError}
                </div>
              ) : null}

              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deletingId !== null}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void confirmRemoveUser()}
                  disabled={deletingId !== null}
                  className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-5 py-3 text-sm font-black text-white shadow-[0_14px_30px_rgba(225,29,72,.2)] disabled:opacity-50"
                >
                  {deletingId !== null ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  {deletingId !== null ? "Eliminando..." : "Eliminar usuario"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

function roleLabel(role: string) {
  if (role === "admin") return "Administrador";
  if (role === "rrhh") return "RRHH";

  return role
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function MetricPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-[108px] max-w-full flex-[1_1_108px] rounded-2xl border border-emerald-100 bg-white/80 px-3 py-2.5 shadow-[0_8px_22px_rgba(16,185,129,.06)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-50 sm:flex-none">
      <div className="text-[11px] font-black uppercase tracking-[0.11em] text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-lg font-black text-slate-950">{value}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
  list,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  list?: string;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        list={list}
        onChange={(e) => onChange(e.target.value)}
        className="field-control"
      />
    </label>
  );
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700 transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100">
      {role}
    </span>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, row) => (
        <tr key={row}>
          {Array.from({ length: cols }).map((__, col) => (
            <td key={col} className="border-b border-[#edf1f3] px-5 py-4">
              <div className="h-5 w-28 animate-pulse rounded-xl bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function getUserPhotoUrl(user: AdminPanelUser): string | null {
  return user.foto_url ?? null;
}

function togglePartnerId(current: number[], partnerId: number) {
  return current.includes(partnerId)
    ? current.filter((id) => id !== partnerId)
    : Array.from(new Set([...current, partnerId]));
}

function formatDate(value: string) {
  return new Date(value).toLocaleString("es-ES");
}

function FileField({
  label,
  file,
  currentPath,
  onChange,
}: {
  label: string;
  file: File | null;
  currentPath?: string | null;
  onChange: (file: File | null) => void;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <div className="rounded-[24px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_8px_20px_rgba(15,23,42,.03)]">
        <div className="flex items-center gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
            <ImagePlus size={20} />
          </div>

          <div className="min-w-0 flex-1">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => onChange(e.target.files?.[0] ?? null)}
              className="block w-full text-sm font-semibold text-slate-500 file:mr-4 file:rounded-xl file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-sm file:font-black file:text-emerald-700 hover:file:bg-emerald-100"
            />

            <div className="mt-2 truncate text-xs font-semibold text-slate-400">
              {file
                ? file.name
                : currentPath
                  ? "Foto actual guardada"
                  : "PNG, JPG o WEBP hasta 4MB"}
            </div>
          </div>
        </div>
      </div>
    </label>
  );
}

function PartnerSelector({
  users,
  editingId,
  selectedIds,
  search,
  onSearch,
  onToggle,
}: {
  users: AdminPanelUser[];
  editingId: number | null;
  selectedIds: number[];
  search: string;
  onSearch: (value: string) => void;
  onToggle: (id: number) => void;
}) {
  const availableUsers = users.filter((user) => user.id !== editingId);

  const filteredUsers = availableUsers.filter((user) =>
    user.nombre.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="block">
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        Parejas de vacaciones
      </span>

      <div className="rounded-[28px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_8px_20px_rgba(15,23,42,.03)]">
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,.035)]">
          <Search size={16} className="text-slate-400" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Buscar empleado compatible..."
            className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400"
          />
        </div>

        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
          {filteredUsers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm font-bold text-slate-400">
              No hay usuarios disponibles.
            </div>
          ) : (
            filteredUsers.map((user) => {
              const selected = selectedIds.includes(user.id);

              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => onToggle(user.id)}
                  className={[
                    "flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all duration-300",
                    selected
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-[0_10px_24px_rgba(16,185,129,.10)]"
                      : "border-slate-200 bg-white text-slate-600 hover:-translate-y-0.5 hover:border-emerald-100 hover:bg-emerald-50/50",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "grid h-5 w-5 place-items-center rounded-md border text-[11px]",
                      selected
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-slate-300 bg-white text-transparent",
                    ].join(" ")}
                  >
                    <Check size={13} />
                  </span>

                  <span className="grid h-9 w-9 place-items-center rounded-xl border border-emerald-100 bg-white text-xs font-black text-emerald-700">
                    {getInitials(user.nombre)}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-black text-slate-900">
                      {user.nombre}
                    </span>
                    <span className="block truncate text-xs font-semibold text-slate-400">
                      {user.correo}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs font-black text-emerald-700">
          {selectedIds.length} empleado(s) compatible(s) seleccionado(s)
        </div>
      </div>
    </div>
  );
}

function ScheduleEditor({
  schedules,
  onChange,
}: {
  schedules: UserSchedulePayload[];
  onChange: (schedules: UserSchedulePayload[]) => void;
}) {
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const usedDays = schedules.map((schedule) => schedule.dia_semana);

  const globalVigenteDesde = useMemo(() => {
    if (schedules.length === 0) return todayIso();

    const firstValue = schedules[0]?.vigente_desde ?? todayIso();
    const allSame = schedules.every(
      (schedule) => (schedule.vigente_desde ?? "") === firstValue,
    );

    return allSame ? firstValue : "";
  }, [schedules]);

  const globalVigenteHasta = useMemo(() => {
    if (schedules.length === 0) return "";

    const firstValue = schedules[0]?.vigente_hasta ?? "";
    const allSame = schedules.every(
      (schedule) => (schedule.vigente_hasta ?? "") === firstValue,
    );

    return allSame ? firstValue : "";
  }, [schedules]);

  const updateSchedule = (
    index: number,
    patch: Partial<UserSchedulePayload>,
  ) => {
    onChange(
      schedules.map((schedule, currentIndex) =>
        currentIndex === index
          ? {
              ...schedule,
              ...patch,
            }
          : schedule,
      ),
    );
  };

  const updateGlobalVigencia = (
    patch: Pick<Partial<UserSchedulePayload>, "vigente_desde" | "vigente_hasta">,
  ) => {
    onChange(
      schedules.map((schedule) => ({
        ...schedule,
        ...patch,
      })),
    );
  };

  const addSchedule = () => {
    const nextDay = WEEK_DAYS.find((day) => !usedDays.includes(day.value));

    if (!nextDay) return;

    onChange([
      ...schedules,
      {
        dia_semana: nextDay.value,
        hora_inicio: "07:30",
        hora_fin: nextDay.value === 5 ? "15:00" : "15:30",
        tolerancia_minutos: 15,
        activo: true,
        vigente_desde: globalVigenteDesde || todayIso(),
        vigente_hasta: globalVigenteHasta || null,
      },
    ]);
  };

  const removeSchedule = (index: number) => {
    onChange(schedules.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <div className="block">
      <div className="mb-2">
        <span className="block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
          Horario laboral
        </span>
      </div>

      <div className="rounded-[28px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_8px_20px_rgba(15,23,42,.03)]">
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          <CalendarClock size={18} className="mt-0.5 shrink-0" />
          <div>
            Por defecto se propone 07:30-15:30 de lunes a jueves y 07:30-15:00 los viernes. Los meses de julio y agosto los viernes son de 7:30-14:30. Puedes ajustar cada día por empleado.
          </div>
        </div>

        <div className="mt-4 flex justify-start">
          <button
            type="button"
            onClick={() => setShowScheduleManager((value) => !value)}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,.08)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100 hover:shadow-[0_12px_24px_rgba(16,185,129,.12)]"
          >
            <CalendarClock size={14} />
            {showScheduleManager
              ? "Ocultar Administrar horario"
              : "Administrar horario"}
          </button>
        </div>

        {!showScheduleManager ? null : (
          <>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <Field
                label="Vigente desde global"
                type="date"
                value={globalVigenteDesde}
                onChange={(value) =>
                  updateGlobalVigencia({
                    vigente_desde: value || todayIso(),
                  })
                }
              />

              <Field
                label="Vigente hasta global"
                type="date"
                value={globalVigenteHasta}
                onChange={(value) =>
                  updateGlobalVigencia({
                    vigente_hasta: value || null,
                  })
                }
              />
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={addSchedule}
                disabled={schedules.length >= 7}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 transition-all hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={14} />
                Añadir día
              </button>
            </div>

            <div className="max-h-[560px] space-y-3 overflow-y-auto pr-1">
              {schedules.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm font-bold text-slate-400">
                  Este usuario no tiene horario configurado.
                </div>
              ) : (
                schedules
                  .slice()
                  .sort((a, b) => a.dia_semana - b.dia_semana)
                  .map((schedule) => {
                    const originalIndex = schedules.findIndex(
                      (item) => item.dia_semana === schedule.dia_semana,
                    );

                    return (
                      <div
                        key={schedule.dia_semana}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_18px_rgba(15,23,42,.025)]"
                      >
                        <div className="grid gap-3 md:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                              Día
                            </span>

                            <select
                              value={schedule.dia_semana}
                              onChange={(e) =>
                                updateSchedule(originalIndex, {
                                  dia_semana: Number(e.target.value),
                                })
                              }
                              className="field-control"
                            >
                              {WEEK_DAYS.map((day) => (
                                <option
                                  key={day.value}
                                  value={day.value}
                                  disabled={
                                    day.value !== schedule.dia_semana &&
                                    usedDays.includes(day.value)
                                  }
                                >
                                  {day.label}
                                </option>
                              ))}
                            </select>
                          </label>

                          <Field
                            label="Entrada"
                            type="time"
                            value={schedule.hora_inicio}
                            onChange={(value) =>
                              updateSchedule(originalIndex, {
                                hora_inicio: value,
                              })
                            }
                          />

                          <Field
                            label="Salida"
                            type="time"
                            value={schedule.hora_fin}
                            onChange={(value) =>
                              updateSchedule(originalIndex, {
                                hora_fin: value,
                              })
                            }
                          />

                          <Field
                            label="Tolerancia"
                            type="number"
                            value={String(schedule.tolerancia_minutos ?? 0)}
                            onChange={(value) =>
                              updateSchedule(originalIndex, {
                                tolerancia_minutos: Number(value || 0),
                              })
                            }
                          />

                          <button
                            type="button"
                            onClick={() => removeSchedule(originalIndex)}
                            className="mt-6 grid h-11 w-11 place-items-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-500 transition-all hover:bg-rose-100"
                            title="Eliminar día"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                          <Field
                            label="Vigente desde"
                            type="date"
                            value={schedule.vigente_desde}
                            onChange={(value) =>
                              updateSchedule(originalIndex, {
                                vigente_desde: value,
                              })
                            }
                          />

                          <Field
                            label="Vigente hasta"
                            type="date"
                            value={schedule.vigente_hasta ?? ""}
                            onChange={(value) =>
                              updateSchedule(originalIndex, {
                                vigente_hasta: value || null,
                              })
                            }
                          />

                          <label className="mt-6 flex h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-black text-slate-600">
                            <input
                              type="checkbox"
                              checked={schedule.activo}
                              onChange={(e) =>
                                updateSchedule(originalIndex, {
                                  activo: e.target.checked,
                                })
                              }
                              className="h-4 w-4 accent-emerald-600"
                            />
                            Activo
                          </label>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

